import React, { useState, useMemo } from 'react';
import { EstateAssets, EstateProfile, IHTCalculation } from '../../types';
import { CheckCircle2, AlertCircle, FileText, ExternalLink, ChevronDown, ChevronUp, Info, MapPin } from 'lucide-react';
import { getProbateStatusExplanation } from './probateLogic';
import { generateFormMappings, FormMapping } from '../../server/services/formMappingService';
import { deriveHasGiftWithReservation } from '../probate/deriveReviewFlags';

// ---------------------------------------------------------------------------
// Data model
// ---------------------------------------------------------------------------

type FormStatus = 'required' | 'likely' | 'check' | 'not_needed';

interface HMRCForm {
 ref: string;
 title: string;
 purpose: string; // one-line description
 reason: string; // why THIS estate needs / doesn't need it
 status: FormStatus;
 group: 'main' | 'supplementary' | 'relief' | 'probate';
 hmrcUrl: string;
}

// ---------------------------------------------------------------------------
// Logic — determine which forms are needed
// ---------------------------------------------------------------------------

interface FormMapperInput {
 routeKey: string | null | undefined;
 assets: EstateAssets;
 profile: EstateProfile;
 ihtCalc?: IHTCalculation | null;
}

function determineForms(input: FormMapperInput): HMRCForm[] {
 const { routeKey, assets, profile, ihtCalc } = input;
 const b = ihtCalc?.breakdown;
 const sp = profile.iht_details?.predeceased_spouse;
 const isIHT400Route = routeKey === 'POST_2022_IHT400';
 const isExcepted = routeKey === 'POST_2022_EXCEPTED';
 const willExists = profile.representatives?.will_exists;

 // Determine probate status for form alignment
 const probateInfo = getProbateStatusExplanation({ assets });
 const probateIsUnclear = probateInfo.status === 'UNCLEAR';

 const hasProperties = assets.properties.length > 0;
 const hasJointAssets =
 assets.properties.some(p => p.ownership_type === 'joint_tenants' || p.ownership_type === 'tenants_in_common') ||
 assets.bank_accounts.some(b => b.ownership === 'joint') ||
 assets.investments.some(i => i.ownership === 'joint');
 const hasBankAccounts = assets.bank_accounts.length > 0;
 const hasInvestments = assets.investments.length > 0;
 const hasPensionsInEstate = assets.pensions.some(p => p.in_estate !== false && p.in_estate !== null);
 const hasBpr = (assets.bpr_assets ?? []).some(a => a.qualifies === true || a.qualifies === null);
 const hasApr = (assets.apr_assets ?? []).some(a => a.qualifies === true || a.qualifies === null);
 const hasForeign = assets.foreign_assets?.exists === true;
 const hasTrusts = assets.trusts_or_life_interest?.exists === true ||
 profile.iht_details?.trust_screening?.has_trusts === true;
 // Phase 1.5 — Discovery"Not sure" answer should drive a review prompt, not
 // an assumed-true requirement. Used to push IHT418 with status='check' when
 // there's no positive trust signal but uncertainty remains.
 const trustsUncertain =
 !hasTrusts && assets.trusts_or_life_interest?.unsure === true;
 // Wave 4 — read GWR through the derived helper so per-property answers
 // (PropertyStep) are not missed when only stored on the property record.
 const hasGWR = deriveHasGiftWithReservation(profile, assets);
 const trustConfidence = profile.iht_details?.trust_screening?.confidence_level;
 const hasGifts =
 (assets.lifetime_gifts ?? []).length > 0 ||
 assets.gifts_last_7_years?.exists === true;
 const hasCharitableRate = b?.charitable_rate_applies === true;
 const hasRnrb = (b?.rnrb_available ?? 0) > 0 || (b?.rnrb_before_taper ?? 0) > 0;
 const hasTransferableNrb = sp?.exists === true && (sp?.unused_nrb_percent ?? 0) > 0;
 // A brought-forward allowance is nil unless claimed (IHTA 1984 s.8G(3)): IHT436 is needed only
 // if the claim is being made; an unanswered claim is shown as 'confirm first', never as made.
 const trnrbClaim = sp?.trnrb_claim ?? null;
 const hasTransferableRnrb = sp?.exists === true && (sp?.unused_rnrb_percent ?? 0) > 0 && trnrbClaim !== 'no';
 const probateRequired =
 assets.properties.some(p => p.ownership_type === 'sole' || p.ownership_type === 'tenants_in_common') ||
 assets.bank_accounts.some(b => b.ownership === 'sole') ||
 assets.investments.some(i => i.ownership === 'sole');

 const forms: HMRCForm[] = [];

 // -----------------------------------------------------------------------
 // GROUP: MAIN RETURN
 // -----------------------------------------------------------------------

 if (isIHT400Route) {
 forms.push({
 ref: 'IHT400',
 title: 'Inheritance Tax account',
 purpose: 'Main IHT return — required for all estates that are not excepted',
 reason: 'Required: this estate does not meet the conditions for an excepted estate and a full return must be filed with HMRC.',
 status: 'required',
 group: 'main',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-inheritance-tax-account-iht400',
 });
 forms.push({
 ref: 'IHT421',
 title: 'Probate summary',
 purpose: 'Required alongside IHT400 for a grant of probate in England, Wales or NI',
 reason: probateIsUnclear
 ? 'Likely required \u2014 confirm once probate need is established.'
 : 'Required: must be submitted with IHT400 before HMRC will confirm the estate for a probate grant.',
 status: probateIsUnclear ? 'likely' : 'required',
 group: 'main',
 hmrcUrl: 'https://www.gov.uk/government/publications/probate-form-iht421-probate-summary',
 });
 }

 if (isExcepted) {
 forms.push({
 ref: 'IHT400',
 title: 'Inheritance Tax account',
 purpose: 'Main IHT return — not required for excepted estates',
 reason: 'Not required: this estate qualifies as an excepted estate under the 2022 rules. No IHT return needs to be filed with HMRC.',
 status: 'not_needed',
 group: 'main',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-inheritance-tax-account-iht400',
 });
 }

 // -----------------------------------------------------------------------
 // GROUP: SUPPLEMENTARY SCHEDULES
 // -----------------------------------------------------------------------

 if (isIHT400Route) {
 // IHT402 — Transferable NRB (with TNRB confidence)
 const tnrbConf = sp?.confidence ?? b?.tnrb_confidence;
 const iht402Reason = hasTransferableNrb
 ? tnrbConf === 'confirmed'
 ? `Required: the estate is claiming ${sp!.unused_nrb_percent}% of the unused nil rate band from the predeceased spouse (confirmed).`
 : tnrbConf === 'possible'
 ? `Likely required: the estate may be able to claim ${sp!.unused_nrb_percent}% of the unused nil rate band — confirm the exact percentage with estate records or HMRC.`
 : `Required: the estate is claiming ${sp!.unused_nrb_percent}% of the unused nil rate band from the predeceased spouse.`
 : sp?.exists === true
 ? 'Likely required: a predeceased spouse has been identified — enter the unused NRB percentage to claim the transfer.'
 : 'Not required: no transferable nil rate band is being claimed.';
 const iht402Status: FormStatus = hasTransferableNrb
 ? 'required'
 : sp?.exists === true ? 'likely' : 'not_needed';
 forms.push({
 ref: 'IHT402',
 title: 'Claim to transfer unused nil rate band',
 purpose: 'Claims unused NRB from a predeceased spouse or civil partner (IHTA84 s8A)',
 reason: iht402Reason,
 status: iht402Status,
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-claim-to-transfer-unused-nil-rate-band-iht402',
 });

 // IHT403 — Gifts
 forms.push({
 ref: 'IHT403',
 title: 'Gifts and other transfers of value',
 purpose: 'Reports gifts made within 7 years of death',
 reason: hasGifts
 ? 'Required: gifts made within 7 years of death have been recorded and must be reported on this schedule.'
 : 'Not required: no gifts within the 7-year window have been recorded.',
 status: hasGifts ? 'required' : 'not_needed',
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-gifts-and-other-transfers-of-value-iht403',
 });

 // IHT404 — Jointly owned assets
 forms.push({
 ref: 'IHT404',
 title: 'Jointly owned assets',
 purpose: 'Reports assets owned jointly with another person',
 reason: hasJointAssets
 ? 'Required: the estate includes jointly owned assets (property, accounts or investments shared with another person).'
 : 'Not required: no jointly owned assets have been recorded.',
 status: hasJointAssets ? 'required' : 'not_needed',
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-jointly-owned-assets-iht404',
 });

 // IHT405 — Property
 forms.push({
 ref: 'IHT405',
 title: 'Houses, land, buildings and interests in land',
 purpose: 'Reports all property owned solely or as a share by the deceased',
 reason: hasProperties
 ? `Required: the estate includes ${assets.properties.length} propert${assets.properties.length !== 1 ? 'ies' : 'y'} that must be reported.`
 : 'Not required: no property assets have been recorded.',
 status: hasProperties ? 'required' : 'not_needed',
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-houses-land-buildings-and-interests-in-land-iht405',
 });

 // IHT406 — Bank accounts
 forms.push({
 ref: 'IHT406',
 title: 'Bank and building society accounts',
 purpose: 'Reports all bank and building society accounts in the estate',
 reason: hasBankAccounts
 ? `Required: the estate includes ${assets.bank_accounts.length} bank account${assets.bank_accounts.length !== 1 ? 's' : ''}.`
 : 'Not required: no bank accounts have been recorded.',
 status: hasBankAccounts ? 'required' : 'not_needed',
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-bank-and-building-society-accounts-iht406',
 });

 // IHT409 — Pensions
 if (hasPensionsInEstate) {
 forms.push({
 ref: 'IHT409',
 title: 'Pensions',
 purpose: 'Reports pension benefits that may form part of the estate',
 reason: 'Check required: at least one pension may form part of the estate. IHT409 is needed if the pension pot forms part of the chargeable estate.',
 status: 'check',
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-pensions-iht409',
 });
 }

 // IHT411 — Listed stocks (check if investments exist)
 if (hasInvestments) {
 forms.push({
 ref: 'IHT411',
 title: 'Listed stocks and shares',
 purpose: 'Reports listed stocks, shares and other quoted securities',
 reason: 'Check required: the estate includes investments — if any are listed/quoted securities, IHT411 is needed.',
 status: 'check',
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-listed-stocks-and-shares-iht411',
 });
 }

 // IHT417 — Foreign assets
 if (hasForeign) {
 forms.push({
 ref: 'IHT417',
 title: 'Foreign assets',
 purpose: 'Reports assets located outside the UK',
 reason: 'Required: the estate includes foreign assets which must be reported on this schedule.',
 status: 'required',
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-foreign-assets-iht417',
 });
 }

 // IHT418 — Trusts
 if (hasTrusts) {
 forms.push({
 ref: 'IHT418',
 title: 'Assets held in trust',
 purpose: 'Reports assets held in a trust in which the deceased had an interest',
 reason: 'Required: the estate includes assets held in trust.',
 status: 'required',
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-assets-held-in-trust-iht418',
 });
 } else if (trustsUncertain) {
 forms.push({
 ref: 'IHT418',
 title: 'Assets held in trust',
 purpose: 'Reports assets held in a trust in which the deceased had an interest',
 reason: 'Trust involvement is uncertain — check whether IHT400/IHT418 or specialist advice is required.',
 status: 'check',
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-assets-held-in-trust-iht418',
 });
 } else if (hasGWR) {
 // GWR-only path (no trust involvement). Surface IHT418 as a review,
 // not a confirmed requirement — the deceased's continued benefit may
 // bring the asset back into the estate but isn't always reported on
 // IHT418.
 forms.push({
 ref: 'IHT418',
 title: 'Assets held in trust',
 purpose: 'Reports assets held in a trust in which the deceased had an interest',
 reason: 'Possible gift with reservation of benefit — check whether IHT418 or specialist inheritance tax advice is required.',
 status: 'check',
 group: 'supplementary',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-assets-held-in-trust-iht418',
 });
 }
 }

 // -----------------------------------------------------------------------
 // GROUP: RELIEFS & SPECIAL RATES
 // -----------------------------------------------------------------------

 if (isIHT400Route) {
 // IHT413 — BPR
 if (hasBpr) {
 forms.push({
 ref: 'IHT413',
 title: 'Business and partnership interests and assets',
 purpose: 'Claims Business Property Relief on qualifying business assets',
 reason: 'Required: Business Property Relief is being claimed — qualifying assets must be reported on this schedule.',
 status: 'required',
 group: 'relief',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-business-and-partnership-interests-and-assets-iht413',
 });
 }

 // IHT414 — APR
 if (hasApr) {
 forms.push({
 ref: 'IHT414',
 title: 'Agricultural relief',
 purpose: 'Claims Agricultural Property Relief on qualifying farmland and buildings',
 reason: 'Required: Agricultural Property Relief is being claimed — qualifying property must be reported on this schedule.',
 status: 'required',
 group: 'relief',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-agricultural-relief-iht414',
 });
 }

 // IHT430 — Charitable reduced rate
 if (hasCharitableRate) {
 forms.push({
 ref: 'IHT430',
 title: 'Reduced rate of Inheritance Tax',
 purpose: 'Claims the reduced 36% IHT rate where 10% or more of the estate passes to charity',
 reason: 'Required: the estate qualifies for the reduced 36% rate — this schedule must be completed to confirm the charitable gift meets the 10% threshold.',
 status: 'required',
 group: 'relief',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-reduced-rate-of-inheritance-tax-iht430',
 });
 }

 // IHT435 — RNRB
 if (hasRnrb) {
 forms.push({
 ref: 'IHT435',
 title: 'Claim for Residence Nil Rate Band',
 purpose: 'Claims the additional allowance where a qualifying home passes to direct descendants',
 reason: 'Required: the Residence Nil Rate Band is being applied — this claim form must be filed with the IHT400.',
 status: 'required',
 group: 'relief',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-claim-for-residence-nil-rate-band-iht435',
 });
 }

 // IHT436 — Transferable RNRB
 if (hasTransferableRnrb) {
 forms.push({
 ref: 'IHT436',
 title: 'Claim to transfer unused Residence Nil Rate Band',
 purpose: 'Claims unused RNRB from a predeceased spouse or civil partner',
 reason: trnrbClaim === 'yes'
 ? `Required: the estate is claiming ${sp!.unused_rnrb_percent}% of the unused Residence Nil Rate Band from the predeceased spouse.`
 : `Needed only if the transfer is claimed: ${sp!.unused_rnrb_percent}% of the predeceased spouse's unused Residence Nil Rate Band has been entered, but whether a claim is being made has not been confirmed. Without a claim it is nil and is left out of the estimate.`,
 status: trnrbClaim === 'yes' ? 'required' : 'likely',
 group: 'relief',
 hmrcUrl: 'https://www.gov.uk/government/publications/inheritance-tax-claim-to-transfer-any-unused-residence-nil-rate-band-iht436',
 });
 }
 }

 // -----------------------------------------------------------------------
 // GROUP: PROBATE APPLICATION
 // -----------------------------------------------------------------------

 if (probateRequired || isIHT400Route) {
 const willKnown = willExists === 'yes' || willExists === 'no';
 const hasWill = willExists === 'yes';

 if (willExists === 'yes' || !willKnown) {
 const pa1pStatus: FormStatus = probateIsUnclear
 ? 'likely'
 : willExists === 'yes' ? 'required' : 'likely';
 const pa1pReason = probateIsUnclear
 ? 'Likely required \u2014 if a grant of probate is needed and there is a will.'
 : willExists === 'yes'
 ? 'Required: the deceased left a will \u2014 use PA1P to apply for a grant of probate.'
 : 'Likely required if there is a will \u2014 use PA1P to apply for a grant of probate.';
 forms.push({
 ref: 'PA1P',
 title: 'Probate application (with a will)',
 purpose: 'Apply for a grant of probate where the deceased left a valid will',
 reason: pa1pReason,
 status: pa1pStatus,
 group: 'probate',
 hmrcUrl: 'https://www.gov.uk/government/publications/form-pa1p-apply-for-probate-by-post-all-executors-using-this-form',
 });
 }

 if (willExists === 'no' || !willKnown) {
 const pa1aStatus: FormStatus = probateIsUnclear
 ? 'likely'
 : willExists === 'no' ? 'required' : 'likely';
 const pa1aReason = probateIsUnclear
 ? 'Likely required \u2014 if a grant of probate is needed and there is no will.'
 : willExists === 'no'
 ? 'Required: the deceased did not leave a will \u2014 use PA1A to apply for letters of administration.'
 : 'Likely required if there is no will \u2014 use PA1A for letters of administration.';
 forms.push({
 ref: 'PA1A',
 title: 'Probate application (no will)',
 purpose: 'Apply for letters of administration where there is no will (intestacy)',
 reason: pa1aReason,
 status: pa1aStatus,
 group: 'probate',
 hmrcUrl: 'https://www.gov.uk/government/publications/form-pa1a-apply-for-probate-where-there-is-no-will',
 });
 }
 }

 return forms;
}

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<FormStatus, { dot: string; badge: string; badgeText: string; label: string }> = {
 required: { dot: 'bg-white0', badge: 'bg-white border-[#E8E4DB]', badgeText: 'text-[#1A1A1A]', label: 'Required' },
 likely: { dot: 'bg-[#7A5A00]', badge: 'bg-[#FAF3E0] border-[#E8D89A]', badgeText: 'text-[#7A5A00]', label: 'Likely required' },
 check: { dot: 'bg-[#3730A3]', badge: 'bg-[#EEF0FA] border-[#C7CBEA]', badgeText: 'text-[#3730A3]', label: 'Check needed' },
 not_needed: { dot: 'bg-[#F3EFE6]', badge: 'bg-[#FAFAF8] border-[#E8E4DB]', badgeText: 'text-[#6B6762]', label: 'Not required' },
};

const GROUP_LABELS: Record<HMRCForm['group'], string> = {
 main: 'Main Return',
 supplementary: 'Supplementary Schedules',
 relief: 'Reliefs & Special Rates',
 probate: 'Probate Application',
};

const FormCard: React.FC<{ form: HMRCForm }> = ({ form }) => {
 const s = STATUS_STYLES[form.status];
 return (
 <div className={`rounded-lg border ${s.badge} p-3`}>
 <div className="flex items-start justify-between gap-3">
 <div className="flex items-start gap-2.5 min-w-0">
 <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${s.dot}`} />
 <div className="min-w-0">
 <div className="flex items-center gap-2 flex-wrap">
 <span className="text-sm font-black text-[#1A1A1A]">{form.ref}</span>
 <span className="text-xs text-[#3F3D39] font-medium leading-snug">{form.title}</span>
 </div>
 <p className="text-[10px] text-[#6B6762] mt-0.5 leading-snug">{form.purpose}</p>
 <p className={`text-[10px] mt-1 leading-snug font-medium ${s.badgeText}`}>{form.reason}</p>
 </div>
 </div>
 <div className="flex flex-col items-end gap-1.5 shrink-0">
 <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${s.badge} ${s.badgeText} whitespace-nowrap`}>
 {s.label}
 </span>
 <a
 href={form.hmrcUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="flex items-center gap-0.5 text-[10px] text-[#3730A3] hover:text-[#3730A3] hover:underline whitespace-nowrap"
 >
 HMRC <ExternalLink className="w-2.5 h-2.5" />
 </a>
 </div>
 </div>
 </div>
 );
};

const ITEM_TYPE_STYLE: Record<string, string> = {
 property: 'bg-white text-[#1A1A1A] border-[#E8E4DB]',
 bank_account: 'bg-[#EEF0FA] text-[#3730A3] border-[#C7CBEA]',
 investment: 'bg-[#EEF0FA] text-[#3730A3] border-[#C7CBEA]',
 pension: 'bg-pink-50 text-pink-700 border-pink-200',
 gift: 'bg-orange-50 text-orange-700 border-orange-200',
 debt: 'bg-white text-[#1A1A1A] border-[#E8E4DB]',
 ownership: 'bg-[#FAF3E0] text-[#7A5A00] border-[#E8D89A]',
 relief: 'bg-teal-50 text-teal-700 border-teal-200',
 estate_summary: 'bg-[#FAFAF8] text-[#3F3D39] border-[#E8E4DB]',
 probate_fact: 'bg-[#EEF0FA] text-[#2E2780] border-[#C7CBEA]',
};

const ITEM_TYPE_LABEL: Record<string, string> = {
 property: 'Property',
 bank_account: 'Bank',
 investment: 'Investment',
 pension: 'Pension',
 gift: 'Gift',
 debt: 'Debt',
 ownership: 'Ownership',
 relief: 'Relief',
 estate_summary: 'Summary',
 probate_fact: 'Probate',
};

const FormMappingCard: React.FC<{ mapping: FormMapping }> = ({ mapping }) => {
 return (
 <div className="rounded-lg border border-[#E8E4DB] bg-white overflow-hidden">
 <div className="px-4 py-2.5 bg-[#FAFAF8] border-b border-[#E8E4DB]">
 <div className="flex items-center gap-2">
 <span className="text-sm font-black text-[#1A1A1A]">{mapping.formCode}</span>
 <span className="text-xs text-[#3F3D39] font-medium">{mapping.formName}</span>
 </div>
 <p className="text-[10px] text-[#6B6762] mt-0.5">{mapping.triggerReason}</p>
 </div>

 {mapping.relatedItems.length > 0 && (
 <div className="px-4 py-2.5">
 <p className="text-[9px] font-bold text-[#AFA99F] uppercase tracking-wider mb-1.5">Related estate entries</p>
 <div className="space-y-1">
 {mapping.relatedItems.map((item, i) => (
 <div key={i} className="flex items-start gap-2">
 <span className={`shrink-0 mt-0.5 text-[8px] font-bold uppercase px-1.5 py-0.5 rounded border ${ITEM_TYPE_STYLE[item.itemType] ?? 'bg-[#FAFAF8] text-[#6B6762] border-[#E8E4DB]'}`}>
 {ITEM_TYPE_LABEL[item.itemType] ?? item.itemType}
 </span>
 <span className="text-xs text-[#3F3D39] leading-snug">{item.label}</span>
 </div>
 ))}
 </div>
 </div>
 )}

 {mapping.suggestedEvidence.length > 0 && (
 <div className="px-4 py-2.5 bg-[#EEF0FA]/50 border-t border-[#E8E4DB]">
 <p className="text-[9px] font-bold text-[#3730A3] uppercase tracking-wider mb-1">Evidence typically needed</p>
 <ul className="space-y-0.5">
 {mapping.suggestedEvidence.map((ev, i) => (
 <li key={i} className="text-[10px] text-[#3F3D39] flex items-start gap-1.5">
 <span className="text-[#3730A3] mt-0.5 shrink-0">•</span>
 {ev}
 </li>
 ))}
 </ul>
 </div>
 )}
 </div>
 );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface Props {
 assets: EstateAssets;
 profile: EstateProfile;
 routeKey?: string | null;
 ihtCalc?: IHTCalculation | null;
}

export const HMRCFormMapper: React.FC<Props> = ({ assets, profile, routeKey, ihtCalc }) => {
 const [showNotNeeded, setShowNotNeeded] = useState(false);
 const [showMapping, setShowMapping] = useState(false);
 const formMappings = useMemo(
 () => generateFormMappings(assets, profile, routeKey, ihtCalc),
 [assets, profile, routeKey, ihtCalc]
 );

 const allForms = determineForms({ routeKey, assets, profile, ihtCalc });
 const needed = allForms.filter(f => f.status !== 'not_needed');
 const notNeeded = allForms.filter(f => f.status === 'not_needed');
 const trustConfidence = profile.iht_details?.trust_screening?.confidence_level;

 const requiredCount = allForms.filter(f => f.status === 'required').length;
 const likelyCount = allForms.filter(f => f.status === 'likely').length;
 const checkCount = allForms.filter(f => f.status === 'check').length;

 if (!routeKey || routeKey === 'NEEDS_MORE_INFO' || routeKey === 'UNKNOWN') {
 return (
 <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
 <div className="bg-[#1A1A1A] px-6 py-4">
 <div className="flex items-center gap-2">
 <FileText className="w-4 h-4 text-white" />
 <h2 className="text-xl font-bold text-white">HMRC Forms Required</h2>
 </div>
 <p className="text-[#AFA99F] text-sm mt-1">Complete the estate details to see which forms apply.</p>
 </div>
 <div className="p-6">
 <div className="flex items-center gap-2 text-sm text-[#7A5A00] bg-[#FAF3E0] border border-[#E8D89A] rounded-lg p-3">
 <AlertCircle className="w-4 h-4 shrink-0" />
 Run the analysis first to determine which HMRC forms are needed for this estate.
 </div>
 </div>
 </div>
 );
 }

 // Group needed forms by section
 const groups = (['main', 'supplementary', 'relief', 'probate'] as HMRCForm['group'][])
 .map(g => ({ group: g, forms: needed.filter(f => f.group === g) }))
 .filter(g => g.forms.length > 0);

 return (
 <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
 <div className="bg-[#1A1A1A] px-6 py-4 border-b border-[#1A1A1A]">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <FileText className="w-4 h-4 text-white" />
 <h2 className="text-xl font-bold text-white">HMRC Forms Required</h2>
 </div>
 <div className="flex items-center gap-2">
 {requiredCount > 0 && (
 <span className="text-xs font-bold bg-[#1A1A1A] text-white rounded-full px-2 py-0.5">
 {requiredCount} required
 </span>
 )}
 {likelyCount > 0 && (
 <span className="text-xs font-bold bg-[#FAF3E0]0 text-white rounded-full px-2 py-0.5">
 {likelyCount} likely
 </span>
 )}
 {checkCount > 0 && (
 <span className="text-xs font-bold bg-[#3730A3] text-white rounded-full px-2 py-0.5">
 {checkCount} to check
 </span>
 )}
 </div>
 </div>
 <p className="text-[#AFA99F] text-sm mt-1">Based on the estate profile and calculated filing route.</p>
 </div>

 <div className="p-6 space-y-6">
 {groups.map(({ group, forms }) => (
 <div key={group}>
 <h3 className="text-[10px] font-bold text-[#AFA99F] uppercase tracking-widest mb-2">
 {GROUP_LABELS[group]}
 </h3>
 <div className="space-y-2">
 {forms.map(form => (
 <FormCard key={form.ref} form={form} />
 ))}
 </div>
 </div>
 ))}

 {/* Not-needed section — collapsible */}
 {notNeeded.length > 0 && (
 <div>
 <button
 onClick={() => setShowNotNeeded(v => !v)}
 className="flex items-center gap-1.5 text-[10px] font-bold text-[#AFA99F] uppercase tracking-widest hover:text-[#3F3D39] transition-colors"
 >
 {showNotNeeded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
 {showNotNeeded ? 'Hide' : 'Show'} {notNeeded.length} forms not required for this estate
 </button>
 {showNotNeeded && (
 <div className="mt-2 space-y-2">
 {notNeeded.map(form => (
 <FormCard key={form.ref} form={form} />
 ))}
 </div>
 )}
 </div>
 )}

 {/* Legend */}
 <div className="pt-2 border-t border-[#E8E4DB] flex flex-wrap gap-x-4 gap-y-1">
 {[
 { status: 'required', label: 'Required — must be filed' },
 { status: 'likely', label: 'Likely required — confirm first' },
 { status: 'check', label: 'Check needed — depends on asset type' },
 ].map(({ status, label }) => {
 const s = STATUS_STYLES[status as FormStatus];
 return (
 <div key={status} className="flex items-center gap-1.5 text-[10px] text-[#6B6762]">
 <div className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
 {label}
 </div>
 );
 })}
 </div>

 {/* Form Mapping Detail */}
 {formMappings.length > 0 && (
 <div className="pt-2 border-t border-[#E8E4DB]">
 <button
 onClick={() => setShowMapping(v => !v)}
 className="flex items-center gap-1.5 text-xs font-bold text-[#3730A3] hover:text-[#3730A3] transition-colors"
 >
 <MapPin className="w-3.5 h-3.5" />
 {showMapping ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
 {showMapping ? 'Hide' : 'Show'} form mapping
 </button>

 {showMapping && (
 <div className="mt-3 space-y-4 animate-in slide-in-from-top-1 duration-200">
 {formMappings.map(mapping => (
 <FormMappingCard key={mapping.formCode} mapping={mapping} />
 ))}

 <p className="text-[10px] text-[#AFA99F] italic leading-relaxed">
 This mapping is indicative and designed to help organise estate information.
 Always verify the final form requirements and contents before submission.
 </p>
 </div>
 )}
 </div>
 )}

 {/* Trust / GWR warning banner */}
 {(trustConfidence === 'specialist_review' || trustConfidence === 'caution') && (
 <div className={`rounded-lg p-3 border text-xs ${trustConfidence === 'specialist_review'
 ? 'bg-white border-[#E8E4DB] text-[#1A1A1A]'
 : 'bg-[#FAF3E0] border-[#E8D89A] text-[#5A4200]'
 }`}>
 <div className="flex items-center gap-1.5 font-bold mb-1">
 <AlertCircle className="w-3.5 h-3.5 shrink-0" />
 {trustConfidence === 'specialist_review'
 ? 'Specialist IHT advice recommended'
 : 'Additional IHT considerations identified'}
 </div>
 <p>
 {trustConfidence === 'specialist_review'
 ? 'Trust arrangements or financial products have been identified that may significantly affect the IHT position. The form requirements above may not be complete — additional schedules or specialist returns may be needed.'
 : 'Trust or gift-with-reservation assets have been identified. These may require additional reporting on the IHT return. Review with a qualified adviser to ensure all relevant schedules are included.'}
 </p>
 </div>
 )}

 <p className="text-[10px] text-[#AFA99F] leading-relaxed">
 Form requirements are determined from your estate details. Always verify with HMRC or a qualified adviser before submitting.
 Additional schedules may be required for circumstances not covered in this tool (e.g. heritage assets, conditional exemptions).
 </p>
 </div>
 </div>
 );
};
